import { createContext, useContext, useMemo } from 'react'
import useMeasurementSignalsBulk from '../hooks/measurementSignals/useMeasurementSignalsBulk'

/**
 * De meetmoment-signalen van deze organisatie, één keer opgehaald en gedeeld.
 *
 * De provider is niet optioneel netjes maar noodzakelijk: zonder hem haalt elk
 * scherm dat meekijkt (de dashboardkaart, de kaart bij de deelnemer, de chips
 * in de Onderzoeken-tab) de antwoorden opnieuw op, en dat vermenigvuldigt de
 * leeskosten met het aantal afnemers.
 *
 * AI-CONTEXT: See docs/patterns.md#context-provider
 * AI-CONTEXT: See docs/patterns.md#dashboard-portfolio-aggregation-card
 */
const MeasurementSignalsContext = createContext({
    rows: [],
    anonymousRows: [],
    loaded: false,
    unavailable: [],
})

/**
 * @param {object} props
 * @param {React.ReactNode} props.children - De boom die de signalen mag lezen
 * @param {string} [props.entityId] - Beperk tot één deelnemer of huishouden. Zet dit op
 *   een detailpagina: dan worden alleen de gegevens van díé persoon gelezen in plaats
 *   van die van de hele organisatie.
 * @param {'persona'|'adress'} [props.entityKind] - Bij een `entityId`: welke soort.
 * @param {boolean} [props.enabled] - `false` haalt niets op. Gebruik dit in plaats van de
 *   provider voorwaardelijk te mounten — dat laatste wisselt het elementtype zodra de
 *   voorwaarde omklapt, en dan bouwt React de hele boom eronder opnieuw op.
 * @param {boolean} [props.includeAnonymous] - Ook de groepsmetingen ophalen (`anonymousRows`).
 *   Kost een lees-ronde per onderzoek dat niemand bereikt, dus alleen aanzetten waar de kaart
 *   'Anonieme onderzoeken' staat.
 */
const MeasurementSignalsProvider = ({ children, entityId, entityKind, enabled = true, includeAnonymous = false }) => {
    const { rows, anonymousRows, loaded, unavailable } = useMeasurementSignalsBulk({
        entityId, entityKind, enabled, includeAnonymous,
    })

    // Gememoïseerd, anders hertekent elke afnemer zodra de ouder om een heel
    // andere reden hertekent (slepen op het dashboard bijvoorbeeld).
    const value = useMemo(
        () => ({ rows, anonymousRows, loaded, unavailable }),
        [rows, anonymousRows, loaded, unavailable]
    )

    return (
        <MeasurementSignalsContext.Provider value={value}>
            {children}
        </MeasurementSignalsContext.Provider>
    )
}

/**
 * De signalen van deze organisatie. Buiten een provider komt de eigen
 * ophaalactie NIET op gang — dan is `loaded` blijvend `false`, wat de schermen
 * lezen als "nog niet bekend" en niet als "niets openstaand".
 *
 * @returns {{rows: Array<object>, anonymousRows: Array<object>, loaded: boolean, unavailable: Array<string>}}
 */
const useMeasurementSignals = () => useContext(MeasurementSignalsContext)

/**
 * De onderzoeken zonder herleidbare invullers — groepsmetingen.
 *
 * Leeg zodra de provider op één deelnemer gefilterd is: daar worden ze niet
 * gelezen. `loaded` hoort erbij, want leeg mag nooit lezen als 'er zijn er geen'
 * zolang de gegevens onderweg zijn.
 *
 * @returns {{rows: Array<object>, loaded: boolean, unavailable: Array<string>}}
 */
const useAnonymousResearchSignals = () => {
    const { anonymousRows, loaded, unavailable } = useMeasurementSignals()
    return { rows: anonymousRows, loaded, unavailable }
}

/**
 * De signalen van één deelnemer of huishouden, op volgorde van meetmoment.
 *
 * @param {string} entityId - Personas.ID of Adresses.ID
 * @returns {{rows: Array<object>, loaded: boolean, unavailable: Array<string>}}
 */
const useMeasurementSignalsFor = (entityId) => {
    const { rows, loaded, unavailable } = useMeasurementSignals()
    const own = useMemo(
        () => (entityId ? rows.filter((row) => row.entityId === entityId) : []),
        [rows, entityId]
    )
    return { rows: own, loaded, unavailable }
}

export default MeasurementSignalsProvider
export {
    MeasurementSignalsContext,
    MeasurementSignalsProvider,
    useMeasurementSignals,
    useMeasurementSignalsFor,
    useAnonymousResearchSignals,
}
